/**
 * Atlas PM · 项目星图 · 交互（射线拾取 / 悬停 / 点击 / 拖拽旋转 / 滚轮缩放）
 *
 * 与 React 解耦：本模块只操作 canvas 与传入的 state 对象，返回统一清理函数。
 * 渲染循环在 scene.ts，读取这里改写的 state。
 */

import * as THREE from 'three';
import type { StarLayoutNode } from './layout';

/** 相机 / 视图可变状态（由渲染循环消费） */
export interface StarViewState {
  rotX: number;
  rotY: number;
  targetRotX: number;
  targetRotY: number;
  dist: number;
  targetDist: number;
  autoRotate: boolean;
  showEdges: boolean;
  showLabels: boolean;
  showClusters: boolean;
  hoveredId: string | null;
  focusedId: string | null;
  dragging: boolean;
}

export function createViewState(): StarViewState {
  return {
    rotX: -0.32,
    rotY: 0,
    targetRotX: -0.32,
    targetRotY: 0,
    dist: 62,
    targetDist: 62,
    autoRotate: true,
    showEdges: true,
    /* 默认关闭全量标签：对齐设计稿 —— 星图靠位置与颜色识别，
       任务名只在悬停/选中时显示（全量显示会严重重叠）。 */
    showLabels: false,
    showClusters: true,
    hoveredId: null,
    focusedId: null,
    dragging: false,
  };
}

export interface StarInputOptions {
  canvas: HTMLCanvasElement;
  camera: THREE.PerspectiveCamera;
  raycaster: THREE.Raycaster;
  state: StarViewState;
  /** 当前全部节点（含中心） */
  getNodes: () => readonly StarLayoutNode[];
  /** 参与射线拾取的对象（各任务/中心 mesh） */
  getPickables: () => readonly THREE.Object3D[];
  onHover: (node: StarLayoutNode | null, clientX: number, clientY: number) => void;
  onClickNode: (node: StarLayoutNode) => void;
  onDoubleClickNode?: (node: StarLayoutNode) => void;
  onBlank: () => void;
  onAutoRotateChange: (on: boolean) => void;
}

export function attachStarInput(o: StarInputOptions): () => void {
  const { canvas, camera, raycaster, state } = o;
  const mouse = new THREE.Vector2(-10, -10);
  let down = false;
  let lastX = 0;
  let lastY = 0;

  const pick = (): StarLayoutNode | null => {
    raycaster.setFromCamera(mouse, camera);
    const hits = raycaster.intersectObjects([...o.getPickables()], false);
    const first = hits[0];
    if (!first) return null;
    const id = first.object.userData?.nodeId as string | undefined;
    if (!id) return null;
    return o.getNodes().find((n) => n.id === id) ?? null;
  };

  const updateMouse = (e: MouseEvent): void => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  };

  const onMouseMove = (e: MouseEvent): void => {
    updateMouse(e);
    if (state.dragging) return;
    const hit = pick();
    const id = hit ? hit.id : null;
    if (id !== state.hoveredId) {
      state.hoveredId = id;
      canvas.style.cursor = hit ? 'pointer' : 'grab';
    }
    o.onHover(hit, e.clientX, e.clientY);
  };

  const onMouseLeave = (): void => {
    state.hoveredId = null;
    o.onHover(null, 0, 0);
  };

  const onClick = (): void => {
    const hit = pick();
    if (hit) o.onClickNode(hit);
    else o.onBlank();
  };

  const onDoubleClick = (): void => {
    const hit = pick();
    if (hit) o.onDoubleClickNode?.(hit);
  };

  const onMouseDown = (e: MouseEvent): void => {
    down = true;
    state.dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
    canvas.style.cursor = 'grabbing';
    if (state.autoRotate) {
      state.autoRotate = false;
      o.onAutoRotateChange(false);
    }
  };

  const onWindowMouseMove = (e: MouseEvent): void => {
    if (!down) return;
    const dx = e.clientX - lastX;
    const dy = e.clientY - lastY;
    lastX = e.clientX;
    lastY = e.clientY;
    state.targetRotY += dx * 0.0056;
    state.targetRotX = Math.max(-1.25, Math.min(1.25, state.targetRotX + dy * 0.0042));
  };

  const onWindowMouseUp = (): void => {
    if (!down) return;
    down = false;
    state.dragging = false;
    canvas.style.cursor = state.hoveredId ? 'pointer' : 'grab';
  };

  const onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    state.targetDist = Math.max(16, Math.min(150, state.targetDist + e.deltaY * 0.055));
  };

  canvas.addEventListener('mousemove', onMouseMove);
  canvas.addEventListener('mouseleave', onMouseLeave);
  canvas.addEventListener('click', onClick);
  canvas.addEventListener('dblclick', onDoubleClick);
  canvas.addEventListener('mousedown', onMouseDown);
  canvas.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('mousemove', onWindowMouseMove);
  window.addEventListener('mouseup', onWindowMouseUp);

  return () => {
    canvas.removeEventListener('mousemove', onMouseMove);
    canvas.removeEventListener('mouseleave', onMouseLeave);
    canvas.removeEventListener('click', onClick);
    canvas.removeEventListener('dblclick', onDoubleClick);
    canvas.removeEventListener('mousedown', onMouseDown);
    canvas.removeEventListener('wheel', onWheel);
    window.removeEventListener('mousemove', onWindowMouseMove);
    window.removeEventListener('mouseup', onWindowMouseUp);
  };
}
